import React, { useState, useEffect } from 'react';
import { Building2, Save, Loader, AlertCircle, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../i18n/hooks/useTranslation';

interface BankAccount {
  accountName: string;
  accountNumber: string;
  bankName: string;
  branchCode?: string;
  swiftCode?: string;
  referencePrefix: string;
}

export const EscrowBankAccountSettings: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [formData, setFormData] = useState<BankAccount>({
    accountName: '',
    accountNumber: '',
    bankName: 'BNZ',
    branchCode: '',
    swiftCode: '',
    referencePrefix: 'ESC'
  });

  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    fetchBankAccount();
  }, []);

  const fetchBankAccount = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/escrow-bank-account`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success && data.data) {
        setFormData({
          accountName: data.data.accountName || '',
          accountNumber: data.data.accountNumber || '',
          bankName: data.data.bankName || 'BNZ',
          branchCode: data.data.branchCode || '',
          swiftCode: data.data.swiftCode || '',
          referencePrefix: data.data.referencePrefix || 'ESC'
        });
      }
    } catch (error) {
      console.error('Fetch bank account error:', error);
      setMessage({ type: 'error', text: 'Failed to load bank account settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setMessage(null);

      const token = localStorage.getItem('token');
      const response = await fetch(`${apiUrl}/api/escrow-bank-account`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setMessage({ type: 'success', text: t('payment.bankAccountSaved') || 'Bank account settings saved successfully' });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ type: 'error', text: data.message || 'Failed to save bank account settings' });
      }
    } catch (error) {
      console.error('Save bank account error:', error);
      setMessage({ type: 'error', text: 'Failed to save bank account settings' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader className="w-8 h-8 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center space-x-3 mb-2">
          <Building2 className="w-6 h-6 text-primary-600" />
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('payment.escrowBankAccountSettings') || 'Escrow Bank Account Settings'}
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              {t('payment.escrowBankAccountSettingsDescription') || 'Configure the bank account details for escrow payments. Clients will use these details for bank transfers.'}
            </p>
          </div>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg flex items-center space-x-2 ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-gray-700 dark:border dark:border-green-800/50 text-green-800 dark:text-green-200' 
            : 'bg-red-50 dark:bg-gray-700 dark:border dark:border-red-800/50 text-red-800 dark:text-red-200'
        }`}>
          {message.type === 'success' ? (
            <CheckCircle className="w-5 h-5" />
          ) : (
            <AlertCircle className="w-5 h-5" />
          )}
          <span>{message.text}</span>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('payment.accountName') || 'Account Name'} *
            </label>
            <input
              type="text"
              required
              value={formData.accountName}
              onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder={t('payment.accountNamePlaceholder') || 'e.g., Company Name Ltd'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('payment.accountNumber') || 'Account Number'} *
            </label>
            <input
              type="text"
              required
              value={formData.accountNumber}
              onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono"
              placeholder={t('payment.accountNumberPlaceholder') || 'e.g., 1234567890'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('payment.bankName') || 'Bank Name'} *
            </label>
            <input
              type="text"
              required
              value={formData.bankName}
              onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder={t('payment.bankNamePlaceholder') || 'e.g., BNZ'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('payment.branchCode') || 'Branch Code'}
            </label>
            <input
              type="text"
              value={formData.branchCode}
              onChange={(e) => setFormData({ ...formData, branchCode: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              placeholder={t('payment.branchCodePlaceholder') || 'Optional'}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('payment.swiftCode') || 'SWIFT Code'}
            </label>
            <input
              type="text"
              value={formData.swiftCode}
              onChange={(e) => setFormData({ ...formData, swiftCode: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono uppercase"
              placeholder={t('payment.swiftCodePlaceholder') || 'e.g., BNZNNZ22'}
              maxLength={11}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
              {t('payment.referencePrefix') || 'Reference Prefix'} *
            </label>
            <input
              type="text"
              required
              value={formData.referencePrefix}
              onChange={(e) => setFormData({ ...formData, referencePrefix: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono uppercase"
              placeholder="ESC"
              maxLength={10}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('payment.referencePrefixDescription') || 'Prefix used for generating payment reference numbers'}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {saving ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>{t('common.saving') || 'Saving...'}</span>
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                <span>{t('common.save') || 'Save Settings'}</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

